import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { changePassword, getEmailProvider } from 'site-modules/shared/components/profile/firebase-auth';
import { insiderMethods } from 'site-modules/shared/components/profile/insider-methods';
import { firebaseValidation } from 'site-modules/shared/components/profile/firebase-validation';

import { validation } from 'site-modules/shared/components/validation/validation';
import { PASSWORD_VALIDATORS } from 'site-modules/shared/components/validation/validators';
import { CHANGE_PASSWORD_ERROR_MAP } from 'site-modules/shared/components/validation/errors-store';

import { settingsWrapperComponent } from 'site-modules/profile/components/settings/settings-wrapper/settings-wrapper';
import { profileScreenMethods } from 'site-modules/shared/components/profile/profile-screen-methods';
import { PasswordForm } from 'site-modules/profile/components/settings/change-password/password-form';

import './change-password.scss';

const PASSWORD_EMPTY_STATE = { oldPassword: '', newPassword: '', confirmPassword: '' };

function ChangePasswordUI({
  firebaseError = null,
  errorMap = new Map(),
  validateForm,
  resetValidation,
  parseFirebaseError,
  validationClasses = {},
  className = null,
  isDeletedUser = false,
  isEditMode = false,
  toggleMode,
  toggleForgotPassword,
  toggleSignModal,
}) {
  const [passwords, setPassword] = useState(PASSWORD_EMPTY_STATE);
  const [showActiveSpinner, toggleActiveSpinner] = useToggle(false);
  const [successActionMessage, setSuccessActionMessage] = useState('');

  function resetErrors() {
    resetValidation();
    parseFirebaseError();
  }

  function onChange({ target: { name, value } }) {
    setPassword({ ...passwords, [name]: value });
  }

  function handleSubmit(e) {
    e.preventDefault();
    toggleActiveSpinner();
    return validateForm(PASSWORD_EMPTY_STATE, passwords, true)
      .then(() => !errorMap.size)
      .then(isValid => {
        if (isValid) {
          const { oldPassword, newPassword } = passwords;
          return changePassword(getEmailProvider().email, oldPassword, newPassword);
        }
        return false;
      })
      .then(response => {
        if (response !== false) {
          resetErrors();
          toggleMode();
          setSuccessActionMessage('Password successfully changed.');
        }
      })
      .catch(error => {
        parseFirebaseError(error);
      })
      .finally(() => {
        toggleActiveSpinner();
      });
  }

  useEffect(() => {
    // Reset the controlled form whenever the settings view enters edit mode.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional form reset on mode change
    setPassword(PASSWORD_EMPTY_STATE);
    if (isEditMode) {
      resetErrors();
      setSuccessActionMessage('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode]);

  const editModeRender = !getEmailProvider() ? (
    <div className="pb-1">Update password is not available for social login users.</div>
  ) : (
    <PasswordForm
      passwords={passwords}
      onChange={onChange}
      onSubmit={handleSubmit}
      onCancel={toggleMode}
      toggleForgotPassword={toggleForgotPassword}
      toggleSignModal={toggleSignModal}
      validationClasses={validationClasses}
      errorMap={errorMap}
      firebaseError={firebaseError}
      showActiveSpinner={showActiveSpinner}
      className={className}
      isDeletedUser={isDeletedUser}
    />
  );

  const viewModeRender = (
    <Fragment>
      <div>**************</div>
      {!!successActionMessage && (
        <p className="success-password-changes-message small text-success">{successActionMessage}</p>
      )}
    </Fragment>
  );

  return isEditMode ? editModeRender : viewModeRender;
}

ChangePasswordUI.propTypes = {
  firebaseError: PropTypes.shape({}),
  validateForm: PropTypes.func.isRequired,
  parseFirebaseError: PropTypes.func.isRequired,
  resetValidation: PropTypes.func.isRequired,
  validationClasses: PropTypes.shape({}),
  errorMap: PropTypes.shape({}),
  className: PropTypes.string,
  isDeletedUser: PropTypes.bool,
  isEditMode: PropTypes.bool,
  toggleMode: PropTypes.func.isRequired,
  toggleForgotPassword: PropTypes.func.isRequired,
  toggleSignModal: PropTypes.func.isRequired,
};

const PasswordFirebaseValidation = validation(
  firebaseValidation(settingsWrapperComponent(ChangePasswordUI)),
  PASSWORD_VALIDATORS,
  CHANGE_PASSWORD_ERROR_MAP
);

export const ChangePassword = profileScreenMethods(insiderMethods(PasswordFirebaseValidation));
